import { hookMethod } from '@merkur/core';

/**
 * Base class of custom error classes, extending the native `Error` class.
 *
 * This class has been introduced to fix the Babel-related issues with
 * extending the native JavaScript (Error) classes.
 *
 * @abstract
 * @class
 * @extends Error
 * @param {string} message The message describing the cause of the error.
 * @param {boolean=} dropInternalStackFrames Whether or not the call stack
 *        frames referring to the constructors of the custom errors should be
 *        excluded from the stack of this error (just like the native platform
 *        call stack frames are dropped by the JS engine).
 *        This flag is enabled by default.
 */
function ExtensibleError(
  message,
  dropInternalStackFrames = true,
) {
  if (!(this instanceof ExtensibleError)) {
    throw new TypeError('Cannot call a class as a function');
  }
  if (this.constructor === ExtensibleError) {
    throw new TypeError(
      'The ExtensibleError is an abstract class and ' +
        'must be extended before it can be instantiated.',
    );
  }

  Error.call(this, message); // super-constructor call;

  /**
   * The name of this error, used in the generated stack trace.
   *
   * @type {string}
   */
  this.name = this.constructor.name;

  /**
   * The message describing the cause of the error.
   *
   * @type {string}
   */
  this.message = message;

  /**
   * Native error instance we use to generate the call stack. For some reason
   * some browsers do not generate call stacks for instances of classes
   * extending the native `Error` class, so we bypass this shortcoming this way.
   *
   * @type {Error}
   */
  this._nativeError = new Error(message);
  this._nativeError.name = this.name;

  // improve compatibility with Gecko
  if (this._nativeError.columnNumber) {
    this.lineNumber = this._nativeError.lineNumber;
    this.columnNumber = this._nativeError.columnNumber;
    this.fileName = this._nativeError.fileName;
  }

  /**
   * The internal cache of the generated stack. The cache is filled upon first
   * access to the {@link ExtensibleError#stack} property.
   *
   * @type {?string}
   */
  this._stack = null;

  /**
   * Whether or not the call stack frames referring to the constructors of
   * the custom errors should be excluded from the stack of this error (just
   * like the native platform call stack frames are dropped by the JS
   * engine).
   *
   * @type {boolean}
   */
  this._dropInternalStackFrames = dropInternalStackFrames;
}

ExtensibleError.prototype = Object.create(Error.prototype);
ExtensibleError.prototype.constructor = ExtensibleError;

/**
 * The call stack captured at the moment of creation of this error. The
 * formatting of the stack is browser-dependant.
 *
 * @var {string} ExtensibleError#stack
 */
Object.defineProperty(ExtensibleError.prototype, 'stack', {
  configurable: true,
  enumerable: false,
  get: function () {
    if (this._stack) {
      return this._stack;
    }

    let stack = this._nativeError.stack;
    if (typeof stack !== 'string') {
      return undefined;
    }

    // drop the stack trace frames referring to the custom error
    // constructors
    if (this._dropInternalStackFrames) {
      let stackLines = stack.split('\n');

      let inheritanceDepth = 1;
      let currentPrototype = Object.getPrototypeOf(this);
      while (currentPrototype !== ExtensibleError.prototype) {
        currentPrototype = Object.getPrototypeOf(currentPrototype);
        inheritanceDepth++;
      }
      stackLines.splice(1, inheritanceDepth);

      this._stack = stackLines.join('\n');
    } else {
      this._stack = stack;
    }

    return this._stack;
  },
});

class GenericError extends ExtensibleError {
  constructor(message, params) {
    super(message);
    const { status = 500, ...otherParams } = params;

    this.name = 'Error';
    this.status = status;

    this._params = otherParams;
  }

  get params() {
    return this._params;
  }
}

const DEV = 'development';
const ENV =
  typeof process !== 'undefined' && process && process.env
    ? process.env.NODE_ENV
    : DEV;

const ERROR_EVENTS = {
  ERROR: '@merkur/plugin-error.error',
};

function errorPlugin() {
  return {
    async setup(widget) {
      widget.error = widget.error
        ? widget.error
        : {
            status: null,
            message: null,
          };

      return widget;
    },
    async create(widget) {
      if (ENV === DEV) {
        if (!widget.$in.component) {
          throw new Error(
            'You must install missing plugin: npm i @merkur/plugin-component',
          );
        }
        if (!widget.$in.eventEmitter) {
          throw new Error(
            'You must install missing plugin: npm i @merkur/plugin-event-emitter',
          );
        }
      }

      hookMethod(widget, 'info', infoHook);
      hookMethod(widget, 'load', loadHook);
      hookMethod(widget, 'mount', mountHook);
      hookMethod(widget, 'update', updateHook);

      return widget;
    },
  };
}

// LIFECYCLE HOOKS

async function loadHook(widget, originalLoad, ...rest) {
  let result = {};
  if (widget.error.status) {
    return result;
  }

  try {
    result = await originalLoad(...rest);
  } catch (error) {
    error.status = error.status || 500;

    setErrorInfo(widget, error);
    emitError(widget, error);
  }

  return result;
}

async function infoHook(widget, originalInfo, ...rest) {
  const result = await originalInfo(...rest);

  return {
    error: widget.error,
    ...result,
  };
}

async function mountHook(widget, originalMount, ...rest) {
  return renderContent(widget, originalMount, rest);
}

async function updateHook(widget, originalUpdate, ...rest) {
  return renderContent(widget, originalUpdate, rest);
}

// HELPER FUNCTIONS

function setErrorInfo(widget, error) {
  widget.error.status = error.status;
  widget.error.message = error.message;
  widget.error.url = error.params && error.params.url;

  if (ENV === DEV) {
    widget.error.stack = error.stack;
  }
}

function emitError(widget, error) {
  widget.emit(ERROR_EVENTS.ERROR, { error });
}

async function renderContent(widget, method, properties) {
  let result = null;

  if (widget.error.status) {
    // error was captured in an earlier lifecycle method
    try {
      // try rendering content, in case the method can handle the error state on its own
      result = await method(...properties);
      return result;
    } catch (err) {
      // content rendering failed
      // do not save the new error info, it would overwrite the previous error
      result = '';
      return result;
    }
  }
  try {
    // no earlier error captured
    result = await method(...properties);
    return result;
  } catch (err) {
    // save error info
    err.status = err.status || 500;
    setErrorInfo(widget, err);
    emitError(widget, err);

    // try rendering again
    return renderContent(widget, method, properties);
  }
}

export { ERROR_EVENTS, GenericError, errorPlugin, renderContent, setErrorInfo };
